import { Anchor, Button, Center, Divider, Group, Portal, Select, Stack, Text } from '@mantine/core';
import { IconExternalLink } from '@tabler/icons-react';
import { ClientHoverCard } from 'components/ClientHoverCard';
import { Panel } from 'components/Panel';
import { i18nKeys, useTranslation } from 'locales';
import { useState } from 'react';
import { isDefined } from 'remeda';
import { PdfDocument } from 'shared/components/PdfDocument';
import type { Reminder } from '../api';
import { ReminderDetailItem } from './ReminderDetailItem';
import { StatusCard } from './StatusCard';

type PostTabProps = {
  reminder: Reminder;
};

export const PostTab = ({ reminder }: PostTabProps) => {
  const { t } = useTranslation();

  const [currentPdfUrl, setCurrentPdfUrl] = useState(reminder.letters[0]?.url || '');
  const currentPdf = reminder.letters.find(({ url }) => url === currentPdfUrl);

  if (!isDefined(currentPdf)) {
    return (
      <Text c="dimmed" fz="sm">
        Error: No recipients found for the selected email.
      </Text>
    );
  }

  return (
    <Panel>
      <Group justify="space-between" align="start">
        <Stack>
          <Portal target="#reminder-download-button-portal">
            <Button
              component="a"
              href={currentPdfUrl}
              leftSection={<IconExternalLink stroke={1.5} size={18} />}
              download
              target="_blank"
              rel="noreferrer"
            >
              {t(i18nKeys.INVOICE.DOWNLOAD_PDF)}
            </Button>
          </Portal>
          <ReminderDetailItem
            label={t(i18nKeys.REMINDER.CLIENT)}
            value={
              <ClientHoverCard debtorId={reminder.debtorId} debtorName={reminder.debtorFullName} />
            }
          />
          <ReminderDetailItem
            label={t(i18nKeys.REMINDER.CHOOSE_PDF)}
            value={
              <Select
                value={currentPdfUrl}
                onChange={v => setCurrentPdfUrl(v!)}
                data={reminder.letters.map((pdf, index) => ({
                  value: pdf.url ?? '',
                  label: `PDF ${index + 1}`,
                }))}
                allowDeselect={false}
              />
            }
          />
          <ReminderDetailItem
            label={t(i18nKeys.REMINDER.TRACKING_LINK)}
            value={
              currentPdf.trackingLink ? (
                <Anchor href={currentPdf.trackingLink} target="_blank">
                  {currentPdf.trackingLink}
                </Anchor>
              ) : (
                <Text>{t(i18nKeys.REMINDER.NO_TRACKING_LINK)}</Text>
              )
            }
          />
        </Stack>
        <StatusCard reminder={reminder} />
      </Group>
      <Divider my="xl" />
      <Center>
        <PdfDocument pdfUrl={currentPdfUrl} />
      </Center>
    </Panel>
  );
};
